// Dashboard drill-through: which link a click on a tile follows.
//
// A tile can carry SEVERAL DRILL_THROUGH entries, one per column
// (`SOURCE_COLUMN`). The visual engine reports the column(s) a click landed
// on (a grid cell's column; a chart point's category, series and value), and
// this module picks the entries that answer it:
//
//   1. entries whose SOURCE_COLUMN is one of the clicked columns;
//   2. otherwise the tile's "Any column" entries (no SOURCE_COLUMN — every
//      model saved before per-column links existed);
//   3. a click that reported NO column (a card, a map pin, a renderer that
//      cannot say) offers every entry of the tile, so no link is unreachable.
//
// One candidate is followed at once; several open DrillChooser, a short menu
// at the pointer. Every host (the platform preview, the smart-page embed, the
// downloaded runtime — which also serves J2EE) uses this one rule.
//
// What a link SENDS is its PARAM_MAPPING: each key a column on the clicked row
// (its value is sent), or one of the CLICKED-COLUMN sources below, which send
// which column was clicked — its NAME or the HEADING people see — so a target
// can be told "the Overdue column" rather than a value on the row.

import { useCallback, useEffect, useRef, useState } from 'react'
import type { CSSProperties } from 'react'

/** What a click reported besides its row (drives per-column drill-through). */
export interface SelectInfo {
  /** The column(s) the click landed on, by NAME, the one clicked FIRST: a grid
   *  cell reports its column; a chart point its value (measure) column, then
   *  its series and category; a pivot cell its measure, then its field. */
  columns?: string[]
  /** The heading shown for each reported column, keyed by its NAME. A column
   *  with no heading here is sent by its name. */
  headings?: Record<string, string>
}

/** PARAM_MAPPING source key that sends the clicked column's NAME. */
export const DRILL_CLICKED_COLUMN = '@clicked_column'
/** PARAM_MAPPING source key that sends the clicked column's HEADING. */
export const DRILL_CLICKED_HEADING = '@clicked_column_heading'

/** The clicked-column sources a mapping may use: `label` is the short word a
 *  designer's mapping row shows (it sits in a narrow box), `hint` the full
 *  sentence for its tooltip. */
export const DRILL_CLICKED_SOURCES: ReadonlyArray<{ key: string; label: string; hint: string }> = [
  { key: DRILL_CLICKED_COLUMN, label: 'Column name', hint: 'Sends the NAME of the column that was clicked, e.g. OVERDUE_QTY' },
  { key: DRILL_CLICKED_HEADING, label: 'Column heading', hint: 'Sends the HEADING of the column that was clicked, e.g. Overdue qty' },
]

/** True when a PARAM_MAPPING key is a clicked-column source, not a row column. */
export function isClickedColumnSource(key: string): boolean {
  const k = key.trim().toLowerCase()
  return k === DRILL_CLICKED_COLUMN || k === DRILL_CLICKED_HEADING
}

export interface DrillEntryLike {
  SOURCE_VISUAL_ID: string
  SOURCE_COLUMN?: string
  TARGET_TYPE?: string
  TARGET_DASHBOARD_ID?: string
  TARGET_VISUAL_ID?: string
  PARAM_MAPPING?: Record<string, string>
}

export type DrillTargetType = 'dashboard' | 'visual' | 'self'

/** The entry's trigger column, trimmed ('' = "Any column"). */
export function drillColumnOf(d: DrillEntryLike): string {
  return typeof d.SOURCE_COLUMN === 'string' ? d.SOURCE_COLUMN.trim() : ''
}

function sameName(a: string, b: string): boolean {
  return a.trim().toLowerCase() === b.trim().toLowerCase()
}

/** Absent / unknown TARGET_TYPE is 'dashboard' (models predating types). */
export function drillTargetType(d: DrillEntryLike): DrillTargetType {
  const t = typeof d.TARGET_TYPE === 'string' ? d.TARGET_TYPE.trim().toLowerCase() : ''
  return t === 'visual' || t === 'self' ? t : 'dashboard'
}

/** The entries a click on `visualId` answers to (see the module comment). */
export function resolveDrillThrough<T extends DrillEntryLike>(
  entries: readonly T[] | null | undefined,
  visualId: string,
  info?: SelectInfo,
): T[] {
  const own = (entries ?? []).filter((d) => d && d.SOURCE_VISUAL_ID === visualId)
  if (!own.length) return []
  const clicked = (info?.columns ?? []).filter((c) => typeof c === 'string' && c.trim() !== '')
  if (clicked.length) {
    const hit = own.filter((d) => {
      const col = drillColumnOf(d)
      return col !== '' && clicked.some((c) => sameName(c, col))
    })
    if (hit.length) return hit
  }
  const anyColumn = own.filter((d) => drillColumnOf(d) === '')
  if (anyColumn.length) return anyColumn
  return clicked.length ? [] : own
}

/** Case-insensitive row field read (data keys are the column NAME casing). */
export function drillRowValue(row: Record<string, unknown>, name: string): unknown {
  if (name in row) return row[name]
  const lk = name.toLowerCase()
  const hit = Object.keys(row).find((k) => k.toLowerCase() === lk)
  return hit ? row[hit] : undefined
}

/** The column a click on `d` stands for: the link's own column when it has
 *  one (it was followed because that column was clicked), otherwise the first
 *  column the click reported. null when the click reported no column (a card,
 *  a map pin, a report tile). */
export function drillClickedColumn(
  d: DrillEntryLike, info?: SelectInfo,
): { name: string; heading: string } | null {
  const reported = (info?.columns ?? []).filter((c) => typeof c === 'string' && c.trim() !== '')
  if (!reported.length) return null
  const own = drillColumnOf(d)
  const name = (own && reported.find((c) => sameName(c, own))) || reported[0]
  const headings = info?.headings ?? {}
  let heading: string | undefined = headings[name]
  if (heading == null) {
    const hit = Object.keys(headings).find((k) => sameName(k, name))
    heading = hit ? headings[hit] : undefined
  }
  const h = typeof heading === 'string' ? heading.trim() : ''
  return { name, heading: h || name }
}

/** PARAM_MAPPING applied to the click: target param → value. A row-column key
 *  sends that column's value on the clicked row; a clicked-column source sends
 *  the clicked column's name or heading (nothing when the click named none). */
export function drillMappedValues(
  d: DrillEntryLike, row: Record<string, unknown>, info?: SelectInfo,
): Record<string, string> {
  const out: Record<string, string> = {}
  for (const [src, target] of Object.entries(d.PARAM_MAPPING || {})) {
    if (!target) continue
    if (isClickedColumnSource(src)) {
      const clicked = drillClickedColumn(d, info)
      if (!clicked) continue
      out[target] = src.trim().toLowerCase() === DRILL_CLICKED_HEADING ? clicked.heading : clicked.name
      continue
    }
    const v = drillRowValue(row, src)
    if (v != null) out[target] = String(v)
  }
  return out
}

/** What a click on a source tile does, for its header hint. */
export function drillKindForTile(
  entries: readonly DrillEntryLike[] | null | undefined, visualId: string,
): 'filter' | 'drill' | undefined {
  const own = (entries ?? []).filter((d) => d && d.SOURCE_VISUAL_ID === visualId)
  if (!own.length) return undefined
  return own.every((d) => drillTargetType(d) === 'self') ? 'filter' : 'drill'
}

/** The line a chooser shows for one entry. `names` turns an obj name into
 *  the object's title where the host knows it. */
export function drillChoiceLabel(d: DrillEntryLike, names?: (obj: string) => string | undefined): string {
  const t = drillTargetType(d)
  if (t === 'self') return 'Filter this dashboard'
  const obj = t === 'visual' ? String(d.TARGET_VISUAL_ID || '') : String(d.TARGET_DASHBOARD_ID || '')
  const words = obj.replace(/^model_/, '').replace(/\.json$/, '').replace(/[_-]+/g, ' ').trim()
  const title = (names && obj ? names(obj) : undefined)
    || (words ? words.charAt(0).toUpperCase() + words.slice(1) : '')
  return `Open ${title || (t === 'visual' ? 'visual' : 'dashboard')}`
}

// Titles of target dashboards, shared by every chooser on the page; keyed by
// `<scope>::<obj>` so two projects never mix. '' = being fetched / unknown.
const TITLE_CACHE = new Map<string, string>()

/** The target dashboards' titles for the chooser ("Open Partner sales" rather
 *  than the object name). The host supplies the loader — the platform reads
 *  the dashboard model; a host without one gets the object name in words.
 *  Prefetched for every dashboard target as soon as the entries are known, so
 *  the menu is right the first time it opens. A failed load just keeps the
 *  fallback. */
export function useDrillTargetTitles(
  scope: string,
  entries: readonly DrillEntryLike[] | null | undefined,
  loadDashboardTitle?: (obj: string) => Promise<string | undefined>,
): (obj: string) => string | undefined {
  const [version, setVersion] = useState(0)
  const targets = (entries ?? [])
    .filter((d) => d && drillTargetType(d) === 'dashboard' && d.TARGET_DASHBOARD_ID)
    .map((d) => String(d.TARGET_DASHBOARD_ID))
  const wanted = Array.from(new Set(targets)).sort().join('|')
  useEffect(() => {
    if (!loadDashboardTitle || !wanted) return undefined
    let live = true
    for (const obj of wanted.split('|')) {
      const key = `${scope}::${obj}`
      if (TITLE_CACHE.has(key)) continue
      TITLE_CACHE.set(key, '')
      loadDashboardTitle(obj)
        .then((title) => {
          const t = (title || '').trim()
          if (t) TITLE_CACHE.set(key, t)
          if (live) setVersion((v) => v + 1)
        })
        .catch((err) => {
          console.warn('Could not read the title of drill-through target', obj, err)
        })
    }
    return () => { live = false }
  }, [scope, wanted, loadDashboardTitle])
  return useCallback(
    (obj: string) => TITLE_CACHE.get(`${scope}::${obj}`) || undefined,
    // `version` re-creates the function once a title arrives.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [scope, version],
  )
}

/** Remembers where the last pointer press was, so a chooser opens there. Put
 *  `onPointerDownCapture` on the element that holds the tiles. */
export function useLastPointer() {
  const ref = useRef<{ x: number; y: number }>({ x: 0, y: 0 })
  const onPointerDownCapture = useCallback((e: { clientX: number; clientY: number }) => {
    ref.current = { x: e.clientX, y: e.clientY }
  }, [])
  return { pointer: ref, onPointerDownCapture }
}

export interface DrillChooserProps<T extends DrillEntryLike> {
  /** Where to open (viewport coordinates). */
  at: { x: number; y: number }
  choices: readonly T[]
  onPick: (choice: T) => void
  onClose: () => void
  names?: (obj: string) => string | undefined
}

/** The short menu shown when one click answers to several drill-throughs. */
export function DrillChooser<T extends DrillEntryLike>({ at, choices, onPick, onClose, names }: DrillChooserProps<T>) {
  const boxRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.stopPropagation(); onClose() } }
    const onDown = (e: PointerEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) onClose()
    }
    document.addEventListener('keydown', onKey, true)
    // Deferred so the click that opened the menu does not also close it.
    const t = window.setTimeout(() => document.addEventListener('pointerdown', onDown, true), 0)
    boxRef.current?.querySelector('button')?.focus()
    return () => {
      window.clearTimeout(t)
      document.removeEventListener('keydown', onKey, true)
      document.removeEventListener('pointerdown', onDown, true)
    }
  }, [onClose])
  // Keep the menu on screen.
  const W = 260
  const vw = typeof window !== 'undefined' ? window.innerWidth : 1200
  const vh = typeof window !== 'undefined' ? window.innerHeight : 800
  const left = Math.max(8, Math.min(at.x, vw - W - 8))
  const top = Math.max(8, Math.min(at.y + 6, vh - (choices.length * 40 + 48)))
  const box: CSSProperties = {
    position: 'fixed', left, top, zIndex: 2000, width: W, padding: 6, borderRadius: 12,
    background: 'var(--color-bg-secondary, #fff)', color: 'var(--color-text, #1c2430)',
    boxShadow: '0 12px 36px rgba(16,24,40,.18), 0 0 0 1px rgba(16,24,40,.08)',
  }
  return (
    <div ref={boxRef} role="menu" aria-label="Open which detail?" style={box}>
      <div style={{
        padding: '4px 8px 6px', fontSize: 12, fontWeight: 700, letterSpacing: '.06em',
        textTransform: 'uppercase', color: 'var(--color-text-faint, #5d6676)',
      }}>
        Open
      </div>
      {choices.map((c, i) => (
        <button
          key={i}
          type="button"
          role="menuitem"
          onClick={() => onPick(c)}
          style={{
            display: 'flex', alignItems: 'center', width: '100%', minHeight: 36, padding: '6px 10px',
            border: 'none', borderRadius: 8, background: 'transparent', color: 'inherit',
            cursor: 'pointer', textAlign: 'left', fontSize: 14, fontWeight: 500,
          }}
          onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--dash-weak, var(--color-surface-2, #f0f3f7))' }}
          onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent' }}
          onFocus={(e) => { e.currentTarget.style.background = 'var(--dash-weak, var(--color-surface-2, #f0f3f7))' }}
          onBlur={(e) => { e.currentTarget.style.background = 'transparent' }}
        >
          {drillChoiceLabel(c, names)}
        </button>
      ))}
    </div>
  )
}
